// src/components/PortalAluno/CarteiraDigital.tsx
// UPDATED: 2026-03-27
// ✅ Dados reais do backend via useStudentProfile
// ✅ Título "IDENTIDADE DE ALUNO" → "IDENTIDADE PROFISSIONAL" quando diploma aprovado
// ✅ QR Code funcional (qrserver.com) apontando para página de verificação
// ✅ Layout melhorado, logo maior, mais informações

import React, { useMemo, useRef } from 'react';
import {
  Download, CheckCircle, Clock, XCircle,
  Loader2, AlertTriangle, RefreshCw, GraduationCap,
} from 'lucide-react';
import { useStudentProfile } from '../../hooks/useStudentProfile';

const statusConfig = {
  ativa:    { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Ativa',    dot: '#22c55e', icon: CheckCircle  },
  pendente: { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   label: 'Pendente', dot: '#f59e0b', icon: Clock        },
  vencida:  { bg: 'bg-red-50',     text: 'text-red-600',     border: 'border-red-200',     label: 'Vencida',  dot: '#ef4444', icon: XCircle      },
};

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const CarteiraDigital: React.FC = () => {
  const carteiraRef = useRef<HTMLDivElement>(null);
  const { student, loading, error, refetch } = useStudentProfile();

  // Diploma aprovado → muda o tipo da carteira
  const diplomaAprovado = useMemo(() =>
    student?.diplomaRequests?.some(r => r.status === 'approved') ?? false,
    [student]
  );

  // Tipo da carteira
  const tipoCarteira    = diplomaAprovado ? 'IDENTIDADE PROFISSIONAL' : 'IDENTIDADE DE ALUNO';
  const subtitleCarteira = diplomaAprovado ? 'Profissional Certificado(a)' : 'Aluno(a) Matriculado(a)';

  // Status da carteira
  const carteiraStatus: 'ativa' | 'pendente' | 'vencida' =
    student?.status === 'active' ? 'ativa' :
    student?.status === 'suspended' ? 'vencida' : 'pendente';
  const sCfg      = statusConfig[carteiraStatus];
  const StatusIcon = sCfg.icon;

  // QR Code aponta para a página de login do aluno
  // Em produção usa o domínio real; em dev usa localhost
  const BASE_URL = import.meta.env.VITE_APP_URL ?? 'https://www.federacaodoscabeleireiros.com.br';
  const qrUrl    = `${BASE_URL}/login/aluno`;
  const qrCodeImg = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&margin=8&color=000000&bgcolor=ffffff&data=${encodeURIComponent(qrUrl)}`;

  // Iniciais
  const initials = student?.fullName
    ?.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() ?? 'AL';

  // Data de expedição = enrollmentDate
  const dataExpedicao = student?.enrollmentDate ? fmt(String(student.enrollmentDate)) : '—';

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
      <Loader2 className="w-8 h-8 text-amber-500 animate-spin"/>
      <p className="text-sm text-gray-400">Carregando carteira…</p>
    </div>
  );

  if (error || !student) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <AlertTriangle className="w-10 h-10 text-amber-400"/>
      <p className="text-sm text-gray-600 font-medium">Não foi possível carregar sua carteira</p>
      <button onClick={refetch}
        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
        <RefreshCw className="w-4 h-4"/> Tentar novamente
      </button>
    </div>
  );

  return (
    <div className="space-y-6 max-w-2xl">

      {/* ── HEADER ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3.5">
        <div className="relative flex-shrink-0">
          <div className="absolute -inset-1 bg-gold-400/20 blur-[10px] rounded-full"/>
          <img src="/icons/carteirinha.png" alt="Carteira Digital"
            className="relative w-11 h-11 object-contain drop-shadow-[0_2px_10px_rgba(212,175,55,0.45)]"
            onError={e => { (e.currentTarget as HTMLImageElement).style.display='none'; }}/>
        </div>
        <div>
          <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">
            Carteira Digital
          </h1>
          <p className="text-xs text-gray-400 mt-1">Sua credencial profissional</p>
        </div>
      </div>

      {/* ── STATUS + TIPO ──────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl
          text-sm font-medium border ${sCfg.bg} ${sCfg.text} ${sCfg.border}`}>
          <StatusIcon className="w-4 h-4 flex-shrink-0"/>
          Carteira {sCfg.label}
        </div>
        {diplomaAprovado && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl
            text-sm font-medium border bg-purple-50 text-purple-700 border-purple-200">
            <GraduationCap className="w-4 h-4 flex-shrink-0"/>
            Profissional Certificado
          </div>
        )}
      </div>

      {/* ── CARTEIRA CARD ──────────────────────────────────────────── */}
      <div ref={carteiraRef}
        className="relative rounded-2xl overflow-hidden shadow-[0_8px_48px_rgba(0,0,0,0.22)]"
        style={{ background: 'linear-gradient(135deg,#0f1b2e 0%,#1a2d42 50%,#0f1b2e 100%)' }}>

        {/* Shimmer top */}
        <div className="absolute top-0 left-0 right-0 h-[2px]
          bg-gradient-to-r from-transparent via-amber-500 to-transparent"/>
        {/* Glow */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 40% at 15% 50%, rgba(212,175,55,0.09) 0%, transparent 65%)' }}/>

        <div className="relative p-6 sm:p-8">

          {/* Cabeçalho da carteira */}
          <div className="flex items-start justify-between mb-7">
            <div>
              <p className="text-[9px] font-bold tracking-[0.28em] text-amber-400/70 uppercase mb-1">
                Federação dos Profissionais da Beleza
              </p>
              <p className="text-base font-black tracking-[0.10em] text-amber-400">
                {tipoCarteira}
              </p>
              <p className="text-[10px] text-white/35 mt-1 tracking-wider">{subtitleCarteira}</p>
            </div>
            {/* Logo maior */}
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 bg-amber-400/20 blur-xl rounded-full"/>
              <img src="/imagem/Bras.png" alt="FEPCSSP"
                className="relative w-20 h-20 object-contain
                  drop-shadow-[0_0_16px_rgba(212,175,55,0.7)]"/>
            </div>
          </div>

          {/* Body */}
          <div className="flex gap-5">
            {/* Foto / Avatar */}
            <div className="flex-shrink-0">
              <div className="w-20 h-24 rounded-xl overflow-hidden border border-white/[0.12]
                shadow-[inset_0_0_0_1px_rgba(212,175,55,0.15)]">
                {student.photoUrl ? (
                  <img src={student.photoUrl} alt="Foto"
                    className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full bg-white/[0.07] flex flex-col items-center justify-center gap-1.5">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg,#f4d03f,#b8860b)' }}>
                      <span className="text-[#1a1a1c] text-sm font-bold">{initials}</span>
                    </div>
                    <p className="text-[8px] text-white/30 uppercase tracking-wider">Foto</p>
                  </div>
                )}
              </div>
            </div>

            {/* Dados */}
            <div className="flex-1 space-y-2.5 min-w-0">
              {[
                { label: 'Nome',      value: student.fullName },
                { label: 'CPF',       value: student.cpf ?? student.cin ?? 'Não informado' },
                { label: 'Registro',  value: student.systemId },
                { label: 'Expedição', value: dataExpedicao },
                { label: 'Curso',     value: student.courseName ?? 'Não informado' },
              ].map(({ label, value }) => (
                <div key={label} className="min-w-0">
                  <p className="text-[8.5px] text-amber-400/55 uppercase tracking-[0.22em] font-semibold">
                    {label}
                  </p>
                  <p className="text-[12.5px] text-white/90 font-medium mt-0.5 leading-tight truncate">
                    {value}
                  </p>
                </div>
              ))}
            </div>

            {/* QR Code funcional */}
            <div className="flex-shrink-0 hidden sm:flex flex-col items-center gap-1.5">
              <div className="w-[100px] h-[100px] rounded-xl bg-white p-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
                {qrCodeImg ? (
                  <img src={qrCodeImg} alt="QR Code"
                    className="w-full h-full object-contain rounded-md"/>
                ) : (
                  <div className="w-full h-full rounded-md bg-gray-100"/>
                )}
              </div>
              <p className="text-[8px] text-white/30 tracking-wider uppercase">Verificar</p>
            </div>
          </div>

          {/* Número + validade rodapé */}
          <div className="flex items-center justify-between mt-6 pt-4
            border-t border-white/[0.08]">
            <p className="text-[9px] text-white/30 font-mono tracking-widest uppercase">
              FEPCSSP · {new Date().getFullYear()}
            </p>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: sCfg.dot }}/>
              <p className="text-[9px] text-white/40 uppercase tracking-wider">{sCfg.label}</p>
            </div>
          </div>
        </div>

        {/* Shimmer bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px]
          bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"/>
      </div>

      {/* QR mobile */}
      <div className="sm:hidden bg-white rounded-2xl border border-gray-100
        shadow-[0_1px_8px_rgba(0,0,0,0.05)] p-5 flex items-center gap-5">
        <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden border border-gray-100">
          {qrCodeImg && (
            <img src={qrCodeImg} alt="QR Code" className="w-full h-full object-contain"/>
          )}
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-800 mb-1">QR Code de verificação</p>
          <p className="text-xs text-gray-400 leading-relaxed">
            Escaneie para verificar a autenticidade desta carteira.
          </p>
          <p className="text-[10px] text-amber-600 font-mono mt-1.5 truncate">{student.systemId}</p>
        </div>
      </div>

      {/* ── AÇÕES ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3">
        {/* Baixar PDF via print */}
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium
            text-[#1a1a1c] hover:shadow-lg transition-all"
          style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}
        >
          <Download className="w-4 h-4"/> Baixar PDF
        </button>
      </div>

      {/* ── CARTEIRA FÍSICA ────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100
        shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden">
        {/* Imagem carteira */}
        <img
          src="/carteira.png"
          alt="Carteira Física FEPCSSP"
          className="w-full object-cover"
          onError={e => {
            const el = e.currentTarget as HTMLImageElement;
            el.style.display = 'none';
          }}
        />
        <div className="p-5 flex flex-col sm:flex-row items-center gap-4">
          <div className="flex-1 text-center sm:text-left">
            <p className="text-base font-black text-gray-900 mb-1">
              Solicite agora mesmo a versão física
            </p>
            <p className="text-sm text-gray-500 leading-relaxed">
              Receba sua carteira profissional impressa com QR Code de validação.
              Entre em contato via WhatsApp para solicitar.
            </p>
          </div>
          <a
            href={(() => {
              const msg = encodeURIComponent(
                `Olá! Gostaria de solicitar a carteira física.\n` +
                `*ID do Aluno:* ${student?.systemId ?? '—'}\n` +
                `*Nome:* ${student?.fullName ?? '—'}\n` +
                `*Escola:* ${student?.affiliate?.schoolName ?? '—'}\n` +
                `*Quero solicitar minha carteira física.*`
              );
              return `https://wa.me/5511999999999?text=${msg}`;
            })()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-bold
              text-white whitespace-nowrap hover:shadow-lg transition-all flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#25d366,#128c7e)' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Solicitar Carteira Física
          </a>
        </div>
      </div>

      {/* Nota sobre QR */}
      <p className="text-xs text-gray-400 flex items-start gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0 mt-1"/>
        O QR Code leva para a página de verificação pública da sua carteira.
        {!diplomaAprovado && ' Quando seu diploma for aprovado, a carteira se torna "Identidade Profissional".'}
      </p>
    </div>
  );
};

export default CarteiraDigital;